<template>
  <div class="file-picker">
    <p class="file-picker__description">{{ header }}</p>
    <div class="action-box" @dragover.prevent @dragenter.prevent @drop.prevent="onDrop" @click="openFileDialog">
      <input
        type="file"
        class="action-box__input"
        ref="fileInputRef"
        :multiple="maxAmount > 1"
        :accept="accept"
        :max="maxSize"
        :maxlength="maxAmount"
        @input="onFileSelect"
      />

      <div class="action-box-placeholder">
        <i class="bx bx-file action-box-placeholder__icon"></i>
        <span class="action-box-placeholder__usege-info">
          <p class="action-box-placeholder__usege-info--blue">Click to upload</p>
          <p class="ml-1 action-box-placeholder__usege-info--black">or Drag & Drop {{ maxAmount > 1 ? 'files' : 'a file' }}</p>
        </span>
        <p class="action-box-placeholder__support-file-info">{{`Support: ${resolveMimeTypeName(accept)} - Up to ${formatBytesHelper(maxSize)}`}}</p>
      </div>

      <div v-if="validatedFiles.length > 0" class="action-box-preview">
        <div
          v-for="(file, index) in validatedFiles"
          :key="index"
          :class="`action-box-preview-card action-box-preview-card--${file.isInvalid ? 'invalid' : 'valid'}`"
        >
          <div :class="`action-box-preview-card__icon-wrapper action-box-preview-card__icon-wrapper--${file.isInvalid ? 'invalid' : 'valid'}`">
            <i :class="`bx bx-file action-box-preview-card__icon action-box-preview-card__icon--${file.isInvalid ? 'invalid' : 'valid'}`"></i>
          </div>
          <div class="action-box-preview-card__info-wrapper">
            <p class="action-box-preview-card__name">{{ file.data.name }}</p>
            <p class="action-box-preview-card__size">{{ formatBytesHelper(file.data.size)}}</p>
          </div>
          <button class="action-box-preview-card__remove-btn" @click.stop="handleRemove(file.id)">
            <i class="bx bx-x action-box-preview-card__remove-icon"></i>
          </button>
        </div>
      </div>
    </div>
    <p v-if="invalidMessage.trim().length > 0" class="file-picker__invalid-message">{{ invalidMessage }}</p>
  </div>
</template>
<script>
import uid from 'uniqid';
import { formatBytesHelper, resolveMimeTypeName, isAcceptedFileType } from '../../utiles/formatHelper';

export default {
  name: 'file-picker',
  props: {
    header: {
      type: String,
    },
    accept: {
      type: Array,
      default() {
        return [];
      },
    },
    maxAmount: {
      type: Number,
      default: 1,
    },
    maxSize: {
      type: Number,
    },
    validatedFiles: {
      type: Array,
      default() {
        return [];
      }
    },
    onChange: {
      type: Function,
    },
    shouldClearSelectedFile: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    invalidMessage() {
      let response = '';
      let isFileInvalidType = false;
      let isFileOversize = false;

      for (const selectedFile of this.validatedFiles) {
        if (selectedFile.isFileInvalidType) isFileInvalidType = selectedFile.isFileInvalidType;
        if (selectedFile.isFileOversize) isFileOversize = selectedFile.isFileOversize;
      }
      if (isFileInvalidType) response += `The file to upload must be in ${resolveMimeTypeName(this.accept)} format`;
      if (isFileOversize) response += `The maximum size for file upload is ${formatBytesHelper(this.maxSize)}`;

      return response;
    },
  },
  methods: {
    formatBytesHelper,
    resolveMimeTypeName,
    selectedFileValidator(selectedFile) {
      const isFileInvalidType = !isAcceptedFileType(selectedFile, this.accept);
      const isFileOversize = selectedFile.size > this.maxSize;
      return {
        id: uid('selected-file-'),
        data: selectedFile,
        isInvalid: isFileInvalidType || isFileOversize,
        isFileInvalidType,
        isFileOversize,
      };
    },
    openFileDialog() {
      this.$refs.fileInputRef.click();
    },
    onFileSelect(event) {
      const availableSlots = this.maxAmount - this.validatedFiles.length;
      if (availableSlots < 1) return; // Guard

      const selectedFiles = [];
      for (const file of event.target.files) {
        selectedFiles.push(file);
      }

      this.handleAdd(selectedFiles, availableSlots);
    },
    onDrop(event) {
      /** draggable element cannot set disable as i know
       * Handle Maximum of file amount will do in this function
       * TODO: POC to find better way
       */
      const availableSlots = this.maxAmount - this.validatedFiles.length;
      if (availableSlots < 1) return; // Guard

      const selectedFiles = [];
      for (const file of event.dataTransfer.files) {
        selectedFiles.push(file);
      }

      this.handleAdd(selectedFiles, availableSlots);
    },
    handleAdd(selectedFiles, availableSlots) {
      const validatedFile = selectedFiles.map((selectedFile) => this.selectedFileValidator(selectedFile));
      const trimFileList = validatedFile.slice(0, availableSlots);
      const addedFiles = [...this.validatedFiles, ...trimFileList];

      this.$emit('onChange', addedFiles);
    },
    handleRemove(selectedFileId) {
      // create new array have only files without removed file.
      const remainingFiles = this.validatedFiles.filter((file) => file.id !== selectedFileId);

      this.$emit('onChange', remainingFiles);
    },
  },
  watch: {
    shouldClearSelectedFile(newVal) {
      if (newVal === true) {
        this.$refs.fileInputRef.value = '';
        this.$emit('onChange', []);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.file-picker {
  width: 100%;

  p {
    margin-bottom: 0px;
  }

  &__description {
    text-align: center;
    font-weight: 500;
    font-size: 16px;
    color: #4b5178;
  }

  .action-box {
    background-color: rgba(33, 150, 243, 0.05);
    border-radius: 8px;
    border-color: #2196f3;
    border-width: 1px;
    border-style: dashed;
    cursor: pointer;
    width: 100%;
    margin-top: 24px;

    &__input {
      display: none;
    }

    .action-box-placeholder {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      width: 100%;
      padding: 24px 0px;

      &__icon {
        color: #2196f3;
        font-size: 30px;
        margin: 0;
      }

      &__usege-info {
        display: flex;
        margin-top: 19px;
        font-weight: 500;
        font-size: 16px;

        &--black {
          color: #4b5178;
        }

        &--blue {
          color: #3371ff;
        }
      }

      &__support-file-info {
        font-size: 14px;
        font-weight: 400;
        color: #646a8c;
      }
    }

    .action-box-preview {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding-bottom: 24px;
      gap: 8px;

      .action-box-preview-card {
        background-color: #fff;
        height: 57px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        position: relative;
        border-width: 0.5px;
        border-style: solid;
        padding: 8px;

        &--valid {
          border-color: #2196f3;
        }

        &--invalid {
          border-color: #d35858;
        }

        &__icon-wrapper {
          width: 32px;
          height: 32px;
          border-radius: 100%;
          display: flex;
          align-items: center;
          justify-content: center;

          &--valid {
            background-color: rgba(33, 150, 243, 0.05);
          }

          &--invalid {
            background-color: rgba(211, 88, 88, 0.05);
          }
        }

        &__icon {
          font-size: 16px;
          margin: 0;

          &--valid {
            color: #2196f3;
          }

          &--invalid {
            color: #d35858;
          }
        }

        &__info-wrapper {
          margin-left: 8px;
          width: 152px;
        }

        &__name {
          color: #4b5178;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &__size {
          color: #868ba6;
          font-size: 13px;
          font-weight: 400;
        }

        &__remove-btn {
          box-shadow: 0px 4px 8px 0px rgba(134, 139, 166, 0.2);
          background: #fff;
          border-radius: 100%;
          border: none;
          width: 16px;
          height: 16px;
          position: absolute;
          top: -8px;
          right: -8px;
          display: flex;
          align-items: center;
          justify-content: center;
          outline: none;
        }

        &__remove-icon {
          font-size: 16px;
          color: #868ba6;
        }
      }
    }
  }

  &__invalid-message {
    color: #d35858;
    font-size: 14px;
    font-weight: 400;
    text-align: start;
    width: 100%;
    margin-top: 8px;
  }
}
</style>
